import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProjectGrid from './components/ProjectGrid';
import StatsCard from './components/StatsCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import { userDashboardService, projectService, activityService } from '../../utils/supabaseQueries';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/user-login');
      return;
    }

    loadDashboardData();
  }, [user, authLoading, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user dashboard data
      const { data: dashboardData, error: dashboardError } = await userDashboardService?.getUserDashboardData();

      if (dashboardError) {
        setError('Failed to load dashboard data. Please try again.');
        return;
      }

      // Format projects for UI
      const formattedProjects = dashboardData?.projects?.map(project => ({
        id: project?.id,
        name: project?.name,
        description: project?.description,
        status: project?.status,
        createdAt: project?.created_at,
        creditsCount: project?.credits?.length || 0,
        thumbnail: project?.thumbnail_url || "https://images.unsplash.com/photo-1524960193066-0596b1e5b77e",
        thumbnailAlt: `Thumbnail for ${project?.name || 'project'}`
      })) || [];

      // Format activities for UI
      const formattedActivities = dashboardData?.activities?.map(activity => ({
        id: activity?.id,
        type: activity?.action,
        message: userDashboardService?.formatActivityMessage(activity),
        timestamp: new Date(activity?.created_at)
      })) || [];

      setProjects(formattedProjects);
      setActivities(formattedActivities);
      setStats(dashboardData?.stats || {});

    } catch (err) {
      setError('Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/credit-creation');
  };

  const handleEditProject = (project) => {
    navigate('/credit-creation', { state: { projectId: project?.id } });
  };

  const handleDuplicateProject = async (project) => {
    try {
      const duplicatedProject = {
        name: `${project?.name} (Copy)`,
        description: project?.description,
        status: 'draft'
      };

      const { data, error } = await projectService?.createProject(duplicatedProject);

      if (error) {
        setError('Failed to duplicate project. Please try again.');
        return;
      }

      // Log activity
      await activityService?.logActivity(data?.id, 'project_created', {
        duplicated_from: project?.id
      });

      // Reload dashboard data
      loadDashboardData();

    } catch (err) {
      setError('Failed to duplicate project. Please try again.');
    }
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Are you sure you want to delete "${project?.name}"?`)) {
      return;
    }

    try {
      const { error } = await projectService?.deleteProject(project?.id);

      if (error) {
        setError('Failed to delete project. Please try again.');
        return;
      }

      // Log activity
      await activityService?.logActivity(project?.id, 'project_deleted', {
        project_name: project?.name
      });

      // Reload dashboard data
      loadDashboardData();

    } catch (err) {
      setError('Failed to delete project. Please try again.');
    }
  };

  const handleViewProject = (project) => {
    navigate('/project-management', { state: { projectId: project?.id } });
  };

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="user" userName="Loading..." />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)]?.map((_, i) => (
                  <div key={i} className="h-24 bg-muted rounded-lg"></div>
                ))}
              </div>
              <div className="h-64 bg-muted rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while dashboard data is loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole={userProfile?.role || "user"} userName={userProfile?.full_name || "User"} />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)]?.map((_, i) => (
                  <div key={i} className="h-24 bg-muted rounded-lg"></div>
                ))}
              </div>
              <div className="h-64 bg-muted rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole={userProfile?.role || "user"} userName={userProfile?.full_name || "User"} />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <Breadcrumb />
          
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <button 
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700"
                >
                  ×
                </button>
              </div>
            </div>
          )}
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your film credit projects and track your progress
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatsCard
              title="Total Projects"
              value={stats?.totalProjects || 0}
              subtitle="All time"
              icon="FolderOpen"
              color="primary"
              trend={null}
              trendValue={null}
            />

            <StatsCard
              title="Completed"
              value={stats?.completedProjects || 0}
              subtitle="Ready to download"
              icon="CheckCircle"
              color="success"
              trend="up"
              trendValue="+12%"
            />

            <StatsCard
              title="Credits Remaining"
              value={stats?.creditsRemaining || 0}
              subtitle="Current plan"
              icon="CreditCard"
              color="info"
              trend={null}
              trendValue={null}
            />

            <StatsCard
              title="Rendering Queue"
              value={stats?.renderingQueue || 0}
              subtitle="In progress"
              icon="Play"
              color="warning"
              trend={null}
              trendValue={null}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Projects Section */}
            <div className="lg:col-span-3">
              <ProjectGrid
                projects={projects}
                onEdit={handleEditProject}
                onDuplicate={handleDuplicateProject}
                onDelete={handleDeleteProject}
                onView={handleViewProject}
                onCreateNew={handleCreateNew}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <QuickActions />
              <ActivityFeed activities={activities} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;